import {
  Popover,
  PopoverTrigger,
  Flex,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  Portal,
} from '@chakra-ui/react';

import { useParams } from 'react-router-dom';
import { Loading } from './components/Loading';
import { useFlowRestartDetailsQuery } from '@/store/query/mco-candidates';
import { EmailBounced, EmailNotFound } from './restart-options/email';
import { AIMessageUnreviewed } from './restart-options/ai';
import {
  InmailCreditsExhausted,
  InmailGeneralError,
  InmailRateLimit,
  InmailProfileNotFound,
  InmailPremiumUnavailable,
} from './restart-options/inmail';
import {
  ConnectionGeneralError,
  ConnectionLimitReached,
  ConnectionRateLimit,
  ConnectionProfileNotFound,
  ConnectionAlreadyConnected,
  ConnectionAlreadyPending,
  ConnectionEmailRequired,
  ConnectionToSelf,
  ConnectionMaxPendingLimitReached,
  ConnectionAlreadyRejected,
  AlreadyPresentInATS,
  LiRetriableError,
} from './restart-options/connection-request';
import { haltReasonValue } from './constant';
import { KText } from 'ui-library/components';

const RestartDetailsComponent = new Map([
  ['email_not_found', EmailNotFound],
  ['email_bounced', EmailBounced],
  ['ai_message_unreviewed', AIMessageUnreviewed],

  ['li_inmail_error', InmailGeneralError],
  ['li_inmail_limit_reached', InmailCreditsExhausted],
  ['li_inmail_rate_limited', InmailRateLimit],
  ['li_inmail_not_found', InmailProfileNotFound],
  ['li_inmail_forbidden', InmailPremiumUnavailable],
  ['li_inmail_retriable_error', LiRetriableError],

  ['li_connection_error', ConnectionGeneralError],
  ['li_connection_limit_reached', ConnectionLimitReached],
  ['li_connection_rate_limited', ConnectionRateLimit],
  ['li_connection_not_found', ConnectionProfileNotFound],
  ['li_connection_already_connected', ConnectionAlreadyConnected],
  ['li_connection_already_pending', ConnectionAlreadyPending],
  ['li_connection_email_required', ConnectionEmailRequired],
  ['li_connection_to_self', ConnectionToSelf],
  ['li_connection_pending_limited', ConnectionMaxPendingLimitReached],
  ['li_connection_already_rejected', ConnectionAlreadyRejected],
  ['li_connection_retriable_error', LiRetriableError],

  ['application_in_ats_job', AlreadyPresentInATS],
]);

export const FlowRestarDetails = ({
  contact = {},
  onStopOpen = () => {},
  openCandidateSlider = () => {},
}) => {
  const { outreachId } = useParams();
  const { isLoading, data } = useFlowRestartDetailsQuery({
    params: {
      outreachId,
      candidateId: contact?.id,
    },
  });

  if (isLoading) {
    return <Loading />;
  }

  const Component = RestartDetailsComponent?.get(
    data?.outreach_candidate?.halt_reason
  );

  if (!Component) {
    return <></>;
  }

  return (
    // eslint-disable-next-line react-hooks/static-components -- RestartDetailsComponent is a module-level Map of fixed components; .get() returns a stable reference, not a per-render component
    <Component
      restartDetails={data}
      onStopOpen={onStopOpen}
      openCandidateSlider={openCandidateSlider}
      contact={contact}
    />
  );
};

export const FlowRestart = ({
  contact = {},
  onStopOpen = () => {},
  openCandidateSlider = () => {},
}) => {
  const { outreach_candidate_stage, halt_reason } = contact || {};

  return (
    <Popover placement="top" isLazy trigger="hover">
      <PopoverTrigger>
        <Flex
          flexDir="column"
          gap="4px"
          data-test-id={`flow-restart-${contact?.id}`}
        >
          <KText
            bg="#FFE6E6"
            borderRadius="20px"
            p="2px 8px"
            fontSize="14px"
            color="#E31B54"
            fontWeight="800"
            w="fit-content"
          >
            {haltReasonValue?.[halt_reason]
              ? 'Action Required'
              : `Flow ${
                  outreach_candidate_stage === 'paused'
                    ? 'is paused'
                    : 'not started'
                }`}
          </KText>
          <KText
            fontSize="14px"
            wordBreak="break-all"
            css={{
              '&:first-letter': {
                textTransform: 'capitalize',
              },
            }}
          >
            {haltReasonValue?.[halt_reason] || ''}
          </KText>
        </Flex>
      </PopoverTrigger>
      <Portal>
        <PopoverContent p="16px" width="350px">
          <PopoverArrow />
          <PopoverBody
            fontSize="14px"
            fontWeight="medium"
            maxH="450px"
            overflow="auto"
          >
            <FlowRestarDetails
              contact={contact}
              onStopOpen={onStopOpen}
              openCandidateSlider={openCandidateSlider}
            />
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
